import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import createAxiosInstance from '../support/AxiosSupport';
import {SparklesIcon} from '../components/icons';
import {pluralForm, pluralItems} from '../utils/plural';
import {useDocumentTitle} from '../hooks/useDocumentTitle';

type IndexWorkerState = 'RUNNING' | 'IDLE' | 'STOPPED';

interface IndexStats {
    total: number;
    indexed: number;
    notIndexed: number;
    pending: number;
    processing: number;
    failed: number;
    workerState?: IndexWorkerState;
    /** Код причины остановки (см. STOPPED_REASONS) либо готовый текст от старого бэкенда. */
    stoppedReason?: string | null;
    /** Отдельное поле под код — на случай, если бэкенд оставит в stoppedReason человеческий текст. */
    stoppedReasonCode?: string | null;
    /** Сколько вещей добавит «Проиндексировать все» прямо сейчас; 0 — кнопке нечего делать. */
    queueable?: number | null;
    lastProcessedAt?: string | null;
}

interface StoppedReasonView {
    /** Подставляется в «Индексация остановлена: …», поэтому со строчной буквы. */
    title: string;
    detail: string;
    cta?: {to: string; label: string};
    /** info — остановка штатная (очередь разобрана), warn — требует внимания. */
    tone: 'info' | 'warn';
}

/**
 * Человеческие тексты причин остановки. Пользователю бесполезен код `NO_EMBEDDING_PROVIDER`:
 * на стенде очередь стояла неделями именно потому, что подписка Claude/Codex не строит
 * эмбеддинги, и ни страница индексации, ни настройки AI об этом не говорили (R3/Q2).
 */
const STOPPED_REASONS: Record<string, (s: IndexStats) => StoppedReasonView> = {
    NO_EMBEDDING_PROVIDER: () => ({
        title: 'нет провайдера эмбеддингов',
        detail: 'Для индексации нужен API-ключ OpenAI — эмбеддинги строит только он. '
            + 'Подписки Claude (Claude Code CLI) и ChatGPT (Codex CLI) их не строят, поэтому очередь стоит.',
        cta: {to: '/settings/profile/ai', label: 'Настройки AI'},
        tone: 'warn',
    }),
    PROVIDER_ERROR: () => ({
        title: 'ошибка провайдера',
        detail: 'Провайдер AI ответил ошибкой — попробуем снова автоматически. '
            + 'Если ничего не меняется, проверьте ключ или подписку в настройках AI.',
        cta: {to: '/settings/profile/ai', label: 'Настройки AI'},
        tone: 'warn',
    }),
    OWNER_DISABLED: () => ({
        title: 'AI-аналитика вещей выключена в настройках',
        detail: 'Фоновая индексация не идёт, пока выключен тумблер «AI-аналитика вещей» в фоновых задачах профиля. '
            + 'Включите его — очередь разберётся сама.',
        cta: {to: '/settings/profile/background-tasks', label: 'Фоновые задачи'},
        tone: 'warn',
    }),
    KILL_SWITCH: () => ({
        title: 'индексация отключена администратором',
        detail: 'AI-обработка временно выключена на стороне сервиса. Вещи можно добавлять '
            + 'и редактировать как обычно — очередь разберётся, когда обработку включат.',
        tone: 'warn',
    }),
    IDLE: (s) => ({
        title: s.notIndexed === 0 ? 'всё проиндексировано' : 'очередь пуста',
        detail: s.notIndexed === 0
            ? 'Новых вещей для обработки нет — воркер ждёт следующую.'
            : 'Непроиндексированные вещи есть, но в очереди их нет: нажмите «Проиндексировать все».',
        tone: 'info',
    }),
};

/** Старый бэкенд присылает готовый текст без кода — показываем его как есть, с прежними ссылками. */
function fallbackReason(text: string): StoppedReasonView {
    return {
        title: text,
        detail: 'Обработка возобновится, когда причина будет устранена.',
        cta: {to: '/settings/profile/ai', label: 'Настройки AI'},
        tone: 'warn',
    };
}

const InlineSpinner: React.FC = () => (
    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
    </svg>
);

function StatCard({label, value, tone}: {label: string; value: number; tone?: 'good' | 'warn' | 'bad'}) {
    const color = tone === 'good' ? 'text-green-500 dark:text-green-400'
        : tone === 'warn' ? 'text-yellow-500 dark:text-yellow-400'
        : tone === 'bad' ? 'text-red-500 dark:text-red-400'
        : 'text-gray-900 dark:text-white';
    return (
        <div className="bg-white dark:bg-ink-800 rounded-xl border border-gray-100 dark:border-white/10 p-4">
            <div className={`text-3xl font-bold ${color}`}>{value}</div>
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</div>
        </div>
    );
}

/**
 * Очередь идёт только когда её действительно разбирают (R3). До фикса признаком «идёт» было
 * «в очереди что-то есть», поэтому остановленная очередь (выключенный тумблер AI-аналитики,
 * недоступный провайдер, kill-switch) месяцами показывала «Индексация идёт…» при нуле в обработке.
 * Старый бэкенд без `workerState` — фолбэк на `processing > 0`.
 */
function isRunning(s: IndexStats): boolean {
    return s.processing > 0 || s.workerState === 'RUNNING';
}

/** Остановленный воркер: причина известна и её нужно показать с действием. */
function stoppedReason(s: IndexStats): StoppedReasonView | null {
    if (s.workerState !== 'STOPPED') return null;
    const code = (s.stoppedReasonCode || s.stoppedReason || '').trim();
    const known = STOPPED_REASONS[code];
    if (known) return known(s);
    return fallbackReason(s.stoppedReason || 'фоновая обработка остановлена');
}

/**
 * Сколько вещей реально ждут очереди. Бэкенд умел отдавать `pending` больше числа
 * непроиндексированных (дубли в очереди, вещи из корзины) — на стенде это читалось как
 * «280 в очереди при 279 непроиндексированных». Показываем согласованное число.
 */
function queuedShown(s: IndexStats): number {
    return Math.min(Math.max(s.pending, 0), Math.max(s.notIndexed, 0));
}

const IndexingPage: React.FC = () => {
    useDocumentTitle();
    const [stats, setStats] = useState<IndexStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [enqueuing, setEnqueuing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const loadStats = useCallback(async () => {
        try {
            const api = await createAxiosInstance();
            const res = await api.get<IndexStats>('/clothing/index/stats');
            setStats(res.data);
            return res.data;
        } catch {
            setError('Не удалось загрузить статистику индексации');
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    // Поллинг, пока очередь действительно разбирается: у остановленного воркера числа не меняются,
    // и опрос каждые 3 секунды был бы бесконечным запросом в пустоту.
    const schedulePoll = useCallback((s: IndexStats | null) => {
        if (pollRef.current) {
            clearTimeout(pollRef.current);
            pollRef.current = null;
        }
        if (s && isRunning(s)) {
            pollRef.current = setTimeout(async () => {
                const next = await loadStats();
                schedulePoll(next);
            }, 3000);
        }
    }, [loadStats]);

    useEffect(() => {
        loadStats().then(schedulePoll);
        return () => { if (pollRef.current) clearTimeout(pollRef.current); };
    }, [loadStats, schedulePoll]);

    const indexAll = async () => {
        setEnqueuing(true);
        setError(null);
        setMessage(null);
        try {
            const api = await createAxiosInstance();
            const res = await api.post<{enqueued: number}>('/clothing/index/all');
            setMessage(res.data.enqueued > 0
                ? `Добавлено в очередь: ${res.data.enqueued}.`
                : 'Новых вещей для индексации нет: все уже проиндексированы или стоят в очереди.');
            const next = await loadStats();
            schedulePoll(next);
        } catch (err: unknown) {
            const e = err as {response?: {data?: {description?: string; message?: string}}};
            setError(e?.response?.data?.description || e?.response?.data?.message || 'Не удалось запустить индексацию');
        } finally {
            setEnqueuing(false);
        }
    };

    const running = !!stats && isRunning(stats);
    const stopped = stats ? stoppedReason(stats) : null;
    const progress = stats && stats.total > 0 ? Math.round((stats.indexed / stats.total) * 100) : 0;
    const queued = stats ? queuedShown(stats) : 0;

    // «Проиндексировать все» имеет смысл, только когда есть что добавить в очередь: иначе кнопка
    // обещает действие, которого не будет (на стенде она была активна при полностью набранной очереди).
    let indexAllBlockedHint: string | null = null;
    if (stats && stats.notIndexed === 0) {
        indexAllBlockedHint = 'Все вещи проиндексированы — добавлять в очередь нечего.';
    } else if (stats && (stats.queueable === 0 || (stats.queueable == null && queued >= stats.notIndexed))) {
        indexAllBlockedHint = `Все непроиндексированные вещи уже в очереди (${queued}) — добавлять нечего.`;
    }

    return (
        <div className="mx-auto max-w-3xl px-4 py-6 space-y-6">
            <div className="flex items-center gap-2">
                <SparklesIcon className="w-6 h-6 text-brand-500"/>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Индексация гардероба</h1>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
                Вещь считается проиндексированной, когда по ней проведена ИИ-аналитика. Только такие вещи
                предлагаются как кандидаты при распознавании состава образа. «Проиндексировать все» запускает
                ИИ-аналитику (платный AI-разбор фото) для вещей без неё — обработка идёт в фоне по очереди.
            </p>

            {/* Ошибка рендерится до блока статистики: при провале загрузки статистики нет, и
                внутри её блока сообщение было бы не видно — страница оставалась пустой. */}
            {!loading && error && <p className="text-sm text-red-500 dark:text-red-400">{error}</p>}

            {loading ? (
                <div className="flex items-center gap-2 text-gray-400"><InlineSpinner/><span>Загрузка…</span></div>
            ) : stats && (
                <>
                    <div className="bg-white dark:bg-ink-800 rounded-2xl border border-gray-100 dark:border-white/10 p-5 space-y-4">
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                Проиндексировано {stats.indexed} из {stats.total}
                            </span>
                            <span className="text-sm font-bold text-brand-500">{progress}%</span>
                        </div>
                        <div className="h-2.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                            <div className="h-2.5 rounded-full bg-brand-500 transition-[width] duration-modal ease-out" style={{width: `${progress}%`}}/>
                        </div>
                    </div>

                    {stopped && (
                        <div
                            role="status"
                            className={stopped.tone === 'warn'
                                ? 'rounded-xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-500/40 dark:bg-amber-900/20'
                                : 'rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-white/10 dark:bg-white/5'}
                        >
                            <p className={stopped.tone === 'warn'
                                ? 'text-sm font-semibold text-amber-800 dark:text-amber-300'
                                : 'text-sm font-semibold text-gray-800 dark:text-gray-100'}>
                                Индексация остановлена: {stopped.title}
                            </p>
                            <p className={stopped.tone === 'warn'
                                ? 'mt-1 text-sm text-amber-700 dark:text-amber-400/90'
                                : 'mt-1 text-sm text-gray-600 dark:text-gray-300'}>
                                {stopped.detail}
                            </p>
                            {queued > 0 && (
                                <p className={stopped.tone === 'warn'
                                    ? 'mt-1 text-sm text-amber-700 dark:text-amber-400/90'
                                    : 'mt-1 text-sm text-gray-600 dark:text-gray-300'}>
                                    В очереди {pluralForm(queued, 'ждёт', 'ждут', 'ждут')} {pluralItems(queued)}
                                    {' '}— обработка начнётся, когда причина устранена.
                                </p>
                            )}
                            <div className="mt-3 flex flex-wrap items-center gap-2">
                                {stopped.cta && (
                                    <Link to={stopped.cta.to} className="btn-secondary min-h-[44px]">
                                        {stopped.cta.label}
                                    </Link>
                                )}
                                <Link
                                    to="/settings/profile/background-tasks"
                                    className="inline-flex min-h-[44px] items-center px-2 text-sm font-medium text-brand-500 hover:text-brand-600 underline"
                                >
                                    Настройки фоновых задач
                                </Link>
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        <StatCard label="Всего вещей" value={stats.total}/>
                        <StatCard label="Проиндексировано" value={stats.indexed} tone="good"/>
                        <StatCard label="Не проиндексировано" value={stats.notIndexed} tone={stats.notIndexed ? 'warn' : 'good'}/>
                        <StatCard label="В очереди" value={queued}/>
                        <StatCard label="Обрабатывается" value={stats.processing}/>
                        <StatCard label="Ошибки" value={stats.failed} tone={stats.failed ? 'bad' : undefined}/>
                    </div>

                    {message && <p className="text-sm text-green-600 dark:text-green-400">{message}</p>}

                    <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-3">
                            <button
                                onClick={indexAll}
                                disabled={enqueuing || !!indexAllBlockedHint}
                                aria-describedby={indexAllBlockedHint ? 'index-all-hint' : undefined}
                                className="min-h-[44px] px-5 py-2.5 rounded-xl bg-brand-500 hover:bg-brand-600 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold transition flex items-center gap-2"
                            >
                                {enqueuing ? <><InlineSpinner/><span>Запускаю…</span></>
                                    : <span>Проиндексировать все{stats.notIndexed ? ` (${stats.notIndexed})` : ''}</span>}
                            </button>
                            {running && (
                                <span className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
                                    <InlineSpinner/> Индексация идёт…
                                </span>
                            )}
                        </div>
                        {indexAllBlockedHint && (
                            <p id="index-all-hint" className="text-sm text-gray-500 dark:text-gray-400">
                                {indexAllBlockedHint}
                            </p>
                        )}
                    </div>

                    {stats.lastProcessedAt && (
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                            Последняя обработанная вещь: {new Date(stats.lastProcessedAt).toLocaleString('ru-RU')}
                        </p>
                    )}
                </>
            )}
        </div>
    );
};

export default IndexingPage;
